/**
 * ProductionDetailsSection - Displays selected option attributes for an order
 * 
 * Shows the production-specific details collected during order configuration
 * like Pantone colors, material choices, etc.
 * These are stored in order.specs._option_attributes
 */
import React from 'react';
import { Palette, Info } from 'lucide-react';

/**
 * Format attribute value for display
 */
function formatAttributeValue(attr) {
  if (!attr) return '-';
  
  // Multi-value with labels
  if (attr.values && attr.labels) {
    return attr.labels.join(', ');
  }
  
  // Single value with label
  if (attr.label) {
    return attr.label;
  }
  
  // Plain value
  if (attr.value) {
    return Array.isArray(attr.value) ? attr.value.join(', ') : attr.value;
  }
  
  return '-';
}

/**
 * Get step label from flow selections or fallback
 */
function getStepLabel(stepId) {
  const labels = {
    format: 'Record Format',
    record_weight: 'Vinyl Weight',
    record_color: 'Vinyl Color',
    vinyl_label: 'Vinyl Label',
    inner_sleeve: 'Inner Sleeve',
    cover: 'Jacket Style',
    cover_finish: 'Cover Finish',
    extras: 'Extras'
  };
  return labels[stepId] || stepId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Get option label from common option IDs
 */
function getOptionLabel(optionId) {
  const labels = {
    black: 'Classic Black',
    color: 'Solid Colour',
    splatter: 'Splatter',
    picture: 'Picture Disc',
    '140g': '140g Standard',
    '180g': '180g Audiophile'
  };
  return labels[optionId] || optionId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function ProductionDetailsSection({ optionAttributes, className = '' }) {
  if (!optionAttributes || Object.keys(optionAttributes).length === 0) {
    return null;
  }

  // Check if there's any actual data to display
  const hasData = Object.values(optionAttributes).some(
    stepData => Object.values(stepData).some(
      optionData => Object.keys(optionData).length > 0
    )
  );

  if (!hasData) {
    return null;
  }

  return (
    <div className={`bg-white border border-slate-200 rounded-xl ${className}`}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <Palette className="w-5 h-5 text-indigo-600" />
        <h3 className="font-semibold text-slate-900">Production Details</h3>
      </div>
      
      <div className="p-4 space-y-4">
        {Object.entries(optionAttributes).map(([stepId, stepData]) => {
          // Skip if no option data
          if (!stepData || Object.keys(stepData).length === 0) return null;
          
          return Object.entries(stepData).map(([optionId, optionData]) => {
            // Skip if no attributes
            if (!optionData || Object.keys(optionData).length === 0) return null;
            
            return (
              <div key={`${stepId}-${optionId}`} className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {getStepLabel(stepId)}
                  </span>
                  <span className="text-xs text-slate-400">→</span>
                  <span className="text-sm font-medium text-slate-700">
                    {getOptionLabel(optionId)}
                  </span>
                </div>
                
                <div className="space-y-1.5">
                  {Object.entries(optionData).map(([fieldId, attr]) => (
                    <div key={fieldId} className="flex items-start gap-2 text-sm">
                      <span className="text-slate-500 min-w-[120px]">
                        {attr.field_label || fieldId.replace(/_/g, ' ')}:
                      </span>
                      <span className="text-slate-900 font-medium">
                        {formatAttributeValue(attr)}
                      </span>
                      {attr.source_list && (
                        <span className="text-xs text-slate-400">
                          ({attr.source_list})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          });
        })}
      </div>
    </div>
  );
}

/**
 * Compact version for inline display
 */
export function ProductionDetailsList({ optionAttributes }) {
  if (!optionAttributes || Object.keys(optionAttributes).length === 0) {
    return <span className="text-slate-400">No production details</span>;
  }

  const items = [];
  
  Object.entries(optionAttributes).forEach(([stepId, stepData]) => {
    if (!stepData) return;
    
    Object.entries(stepData).forEach(([optionId, optionData]) => {
      if (!optionData) return;
      
      Object.entries(optionData).forEach(([fieldId, attr]) => {
        items.push({
          label: attr.field_label || fieldId,
          value: formatAttributeValue(attr)
        });
      });
    });
  });

  if (items.length === 0) {
    return <span className="text-slate-400">No production details</span>;
  }

  return (
    <div className="space-y-1">
      {items.map((item, idx) => (
        <div key={idx} className="text-sm">
          <span className="text-slate-500">{item.label}:</span>{' '}
          <span className="text-slate-900">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

export default ProductionDetailsSection;
